<template>
  <div class="grossing-portion-editable-grid">
    <b-row class="d-flex align-items-center mb-2 mt-1">
      <b-col>
        <b-form-row>
          <b-col cols="auto">
            <span class="ap-title ap-title__main mr-3">{{ $tc('global.block',2) }}</span>
          </b-col>
          <b-col>
            <small class="subtitle">{{ gridSubtitle }}</small>
          </b-col>
        </b-form-row>
      </b-col>
      <b-col
        cols="auto"
        class="d-flex justify-content-end"
      >
        <div class="d-flex">
          <b-button
            v-if="$_canPrintPortionCassette()"
            qa="sample-processing-tab-portion-print-portions-button"
            class="btn__icon btn__icon--primary  px-2"
            :disabled="disabledPrintPortionsCassette"
            @click="showPrintModalFromSelected(false)"
          >
            <i
              v-tooltip="$tc('grossing.portionGrid.printPortion',2)"
              class="far fa-ticket-alt"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            v-if="$_canPrintPortionCassette()"
            qa="sample-processing-tab-portion-print-modal-button"
            class="btn__icon btn__icon--primary  px-2"
            :disabled="disabledPrintPortionsCassette"
            @click="showPrintModalFromSelected(true)"
          >
            <i
              v-tooltip="$tc('grossing.portionGrid.magazineConfiguration')"
              class="icon-ticket-setup"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            v-tooltip="$tc('grossing.portionGrid.techniques')"
            v-shortkey="['alt', 'a']"
            qa="sample-processing-tab-portion-assays-button"
            class="btn__icon btn__icon--primary px-2"
            @shortkey.prevent="showAssaysModal"
            @click="showAssaysModal"
          >
            <i
              class="fas fa-flask"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            v-if="visiblePortionOperations"
            id="request-others"
            :disabled="disabledPortionOperationEllipsis"
            qa="sample-processing-tab-portion-ellipsis-button"
            class="btn__icon btn__icon--primary  px-2"
          >
            <i
              class="fas fa-ellipsis-v"
              aria-hidden="true"
            />
          </b-button>
          <b-popover
            v-if="visiblePortionOperations"
            target="request-others"
            placement="bottom"
            triggers="click blur"
          >
            <div class="ap-popover">
              <ul>
                <li
                  v-if="$_canCreateIncident()"
                >
                  <b-button
                    qa="sample-processing-tab-portion-add-incident-button"
                    :disabled="disabledCreateIncidentToPortion"
                    @click="showIncidentFormModal()"
                  >
                    {{ $tc('grossing.portionGrid.registerIncidents', 2) }}
                  </b-button>
                </li>
                <li
                  v-if="$_canAttachDocumentToPortion()"
                >
                  <b-button
                    qa="sample-processing-tab-portion-add-attachment-button"
                    :disabled="disabledDocumentAttachmentToPortion"
                    @click="showPortionAttachmentModal()"
                  >
                    {{ $tc('grossing.portionGrid.addAttachment') }}
                  </b-button>
                </li>
                <li>
                  <b-button
                    qa="sample-processing-tab-global-locator-button"
                    @click="showInGlobalLocator(selectedPortions[0].label)"
                  >
                    {{ $tc('global.showInGlobalLocator') }}
                  </b-button>
                </li>
              </ul>
            </div>
          </b-popover>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        md="6"
        lg="4"
        xl="2"
      >
        <div>
          <b-input-group
            v-if="$_canProcessSample()"
            class="verify d-flex align-items-center"
          >
            <b-form-input
              id="grossing-portion-verify-closing-input"
              ref="verifyClosingInput"
              v-model="portionLabelToVerify"
              qa="sample-processing-tab-portion-verify-input"
              :disabled="$_disabledVerifyPortions(portions)"
              size="sm"
              :placeholder="`${$tc('grossing.content.verification')}...`"
              @keyup.enter="verifyClosedCassetteByLabel"
            />
            <b-button
              class="btn__icon btn__icon--primary ml-2 mr-0 p-0"
              @click="verifyClosedCassetteByLabel"
            >
              <i
                class="fas fa-check-square"
                aria-hidden="true"
              />
            </b-button>
          </b-input-group>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <hr>
      </b-col>
    </b-row>
    <b-row class="mb-2 d-flex justify-content-between">
      <b-col
        md="12"
        lg="10"
      >
        <b-form-row v-if="$_canCreatePortion()">
          <b-col cols="auto">
            <vt-input
              v-model="cloneNumber"
              qa="sample-processing-tab-portion-number-to-clone-input"
              :disabled="disabledPortionCreation"
              class="form-control-sm clone-number"
              type="number"
              name="”cloneNumber”"
              placeholder="1"
            />
          </b-col>
          <b-col
            lg="4"
            md="4"
            class="my-2 my-md-0"
          >
            <vt-combo-select
              v-model="selectedPortionTypeForAdding"
              qa="sample-processing-tab-portion-type-select"
              :disabled="!canPortionsBeCreated"
              class="nowrap mb-md-2 mb-lg-0"
              :placeholder="$tc('global.portionType')"
              :options="activePortionTypes"
              track-by="potId"
              label="potName"
              @input="onSelectedPortionTypeForAddingComboSelect"
            />
          </b-col>
          <b-col
            lg="4"
            md="4"
          >
            <vt-combo-select
              v-model="tissueProcessorSelected"
              qa="sample-processing-tab-portion-processor-select"
              :disabled="disabledTissueProcessorCombo"
              class="nowrap"
              :options="tissueProcessors"
              track-by="device"
              :placeholder="$tc('grossing.portionGrid.tissueProcessor')"
              label="device"
            />
          </b-col>
          <b-col
            cols="auto"
            class="mt-2 mt-md-0"
          >
            <vt-loading-button
              v-if="$_canCreatePortion()"
              qa="sample-processing-tab-portion-add-button"
              class="d-flex align-items-center btn--primary btn-sm"
              :disabled="disabledPortionCreation"
              :params="{ promise: addNewRows, shortkeys: ['ctrl', '+'] }"
            >
              <i
                class="fal fa-plus mr-2"
                aria-hidden="true"
              />
              {{ $tc('global.block',1) }}
            </vt-loading-button>
          </b-col>
        </b-form-row>
      </b-col>
      <b-col class="d-flex align-items-center justify-content-end">
        <!-- TODO: Cancellation front development required -->
        <b-button
          v-if="false"
          v-tooltip="$tc('global.cancellation.cancelBlocks')"
          class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
          @click="openCancellationModalForm()"
        >
          <i
            class="far fa-times-circle"
            aria-hidden="true"
          />
        </b-button>
        <b-button
          v-if="$_canDeletePortion()"
          v-tooltip="$tc('grossing.portionGrid.deletePortion',2)"
          qa="sample-processing-tab-portion-delete-button"
          class="btn__icon btn__icon--primary p-0 px-2"
          :disabled="disabledPortionDeletion"
          @click="deleteSelectedPortions"
        >
          <i
            class="far fa-trash-alt"
            aria-hidden="true"
          />
        </b-button>
      </b-col>
    </b-row>

    <vt-grid
      qa="sample-processing-tab-portion-grid"
      class="grossing-portion-grid"
      component-code="uicomponent.grossing.portiongrid"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :row-data="portions"
      :framework-components="frameworkComponents"
      :suppress-keyboard-event="backspaceFunction"
      :row-class-rules="rowClassRules"
      @rowSelected="updateGridSubtitle"
      @keydown.native="vtGridKeyDown = $event"
    />

    <ModalPrintMagazine
      ref="modalPrint"
      :laboratory-id="laboratoryId"
      @magazineSelected="printCassetteLabels($event)"
    />
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="ATTACHMENT_ENTITY_TYPE"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded"
      />
    </modal-wrapper>
    <common-attachment-tree-modal
      v-if="patient"
      ref="attachmentTreeModal"
      :patient="patient"
      :laboratory-id="laboratoryId"
      @attachments-updated="loadPortions"
    />
    <modal-confirmation
      id="onDeletingPortion"
      ref="onDeletingPortion"
      :content="portionDeletionConfirmationMessage"
    />
    <modal-confirmation
      id="saveAndPrintConfirmation"
      ref="saveAndPrintConfirmation"
      :content="$tc('grossing.modal.saveAndPrintConfirmation')"
    />
    <print-component ref="printComponent" />
    <assays-view-modal
      v-if="caseId"
      ref="modalAssaysView"
      :cas-id="caseId"
      :priority-cas="priority"
      @portionsUpdated="onAssaysViewModalPortionsUpdated"
      @subportionsUpdated="onAssaysViewModalSubportionsUpdated"
      @subportionsAdded="onAssaysViewModalSubportionsAdded"
    />
    <modal-wrapper ref="itemsForShippingModal">
      <items-for-shipping-modal
        :portions="portionsToSend"
        :cas-id="caseId"
        :sam-id="sampleId"
        :lab-id="laboratoryId"
      />
    </modal-wrapper>
    <laboratory-cancellation-modal-form
      ref="laboratoryCancellationModalForm"
      :elements-number="selectedPortions.length"
    />
  </div>
</template>

<script>
import VtGrid from '@/components/VtGrid/VtGrid'
import ModalPrintMagazine from '@/modules/sampleProcessingModule/views/form/modals/ModalPrintMagazine'
import VtSelectCellEditor from '@/cellEditors/common/VtSelectCellEditor'
import DatePickerRenderer from '@/cellRenderers/registry/DatePickerRenderer'
import fragmentOrientationService from '@/api/resources/common/fragmentOrientationService'
import portionTypeService, { PORTION_TYPES } from '@/api/resources/portionTypeService'
import sampleProcessingPortionService from '@/modules/sampleProcessingModule/services/sampleProcessingPortionService'
import { cloneDeep, some } from 'lodash'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import AddFileModal from '@/components/AddFileModal'
import CommonAttachmentTreeModal from '@/components/attachments/CommonAttachmentModalTree'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import { getPortionDeletionConfirmationMessage } from '@/api/resources/ConfirmationMessageResource'
import AssaysViewModal from '@/components/assays-view/components/modal/assays-view-modal.vue'
import NumericCellEditor from '@/cellEditors/numeric/numeric-cell-editor.vue'
import ItemsForShippingModal from '@/modules/shipments-module/old/views/components/ItemsForShippingModal'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { commonDeviceService } from '@/api/services/common/device/common-device.service'
import { PortionOperation } from '@/api/operations/models/portion-operation'
import { SampleOperationGuardMixin } from '@/api/operations/mixins/guards/sample-operation-guard.mixin'
import { PortionOperationGuardMixin } from '@/api/operations/mixins/guards/portion-operation-guard.mixin'
import { ShipmentOperationGuardMixin } from '@/api/operations/mixins/guards/shipment-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import VtSelectAsynchronousCellEditor from '@/cellEditors/common/VtSelectAsynchronousCellEditor'
import { AgGridColumnType, AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { TextNormalization } from '@/shared/util/text-normalization'
import { PortionTypeEnum } from '@/api/services/common/portion-type/common-portion-type.dto'
import LaboratoryCancellationModalForm from '@/components/laboratory-cancellation-modal-form/laboratory-cancellation-modal-form.vue'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'

export default {
    name: 'SampleProcessingTabPortionEditableGrid',
    components: {
        VtGrid,
        ModalPrintMagazine,
        AddFileModal,
        CommonAttachmentTreeModal,
        ModalConfirmation,
        AssaysViewModal,
        ItemsForShippingModal,
        LaboratoryCancellationModalForm
    },
    mixins: [
        SampleOperationGuardMixin,
        PortionOperationGuardMixin,
        ShipmentOperationGuardMixin,
        IncidentOperationGuardMixin
    ],
    props: {
        disabled: { type: Boolean, default: false },
        laboratoryId: { type: Number, required: true },
        patient: { type: Object, required: true },
        sample: { type: Object, required: true },
        caseId: { type: Number, required: true },
        priority: { type: Object, required: true }
    },
    data() {
        return {
            portionsToSend: [],
            vtGridKeyDown: null,
            creatingPortions: false,
            initPorIdSelect: null,
            gridSubtitle: '',
            showVerifyClosingTooltip: false,
            portionLabelToVerify: '',
            popOption: true,
            rowClassRules: true,
            showErrors: false,
            frameworkComponents: null,
            autoGroupColumnDef: null,
            getRowHeight: null,
            detailHeightPortions: null,
            columnDefs: [],
            portions: [],
            selectedPortions: [],
            originalPortions: [],
            fragmentOrientations: [],
            portionTypes: [],
            activePortionTypes: [],
            virtualPortionType: null,
            cloneNumber: 1,
            selectedPortionTypeForAdding: null,
            portionDeletionConfirmationMessage: this.$tc('grossing.modal.deletePortion'),
            entitiesIdArray: [],
            tissueProcessorSelected: null,
            tissueProcessors: []
        }
    },
    computed: {
        sampleId() { return this.sample.id },
        sampleTypeId() { return this.sample.sampleType.id },
        topographicCodeId() { return this.sample.topographicCode.id },
        canPortionsBeCreated() { return this.sample.metadata.operationGuards.canPortionsBeCreated },
        laboratoryIdSample() { return this.sample.currentLaboratory.labId },
        disabledTissueProcessorCombo() {
            return this.selectedPortionTypeForAdding?.portionTypeEnum === PortionTypeEnum.FROZEN_BLOCK
        },
        selectedUniquePortion() {
            return this.selectedPortions.length === 1 ? this.selectedPortions[0] : null
        },
        disabledPrintPortionsCassette() {
            return !this.selectedPortions.length ||
                this.$_disabledPrintPortionsCassette(this.selectedPortions)
        },
        disabledPortionOperationEllipsis() {
            return !this.selectedPortions.length ||
                (this.disabledCreateIncidentToPortion && this.disabledDocumentAttachmentToPortion)
        },
        disabledPortionCreation() {
            return !this.selectedPortionTypeForAdding ||
                this.creatingPortions ||
                !this.canPortionsBeCreated
        },
        disabledPortionDeletion() {
            return !this.selectedPortions.length ||
                this.$_disabledDeletePortions(this.selectedPortions) ||
                this.selectedPortionsCannotBeDeleted
        },
        visiblePortionOperations() {
            return this.$_canCreateIncident() ||
                this.$_canAttachDocumentToPortion()
        },
        disabledCreateIncidentToPortion() {
            return !this.selectedUniquePortion ||
                !this.$_canBeIncidentCreatedToPortion(this.selectedUniquePortion)
        },
        disabledDocumentAttachmentToPortion() {
            return !this.selectedUniquePortion ||
                !this.$_canBeDocumentAttachedToPortion(this.selectedUniquePortion)
        },
        ATTACHMENT_ENTITY_TYPE() {
            return ATTACHMENT_ENTITY_TYPE.PORTION
        },
        selectedPortionsCannotBeDeleted() {
            return !!this.selectedPortions.find(portion => portion.portionType.potId === this.virtualPortionType.potId)
        },
        gridOptions() {
            return {
                onGridReady: this.onGridReady,
                stopEditingWhenGridLosesFocus: true,
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getPortionsContextMenu,
                pagination: false,
                rowHeight: 30,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                autoGroupColumnDef: {
                    headerName: 'Consoles', // custom header name for group
                    pinned: 'left', // force pinned left. Does not work in columnDef
                    cellRendererParams: {
                        suppressCount: true // remove number in Group Column
                    }
                },
                onCellEditingStopped: params => {
                    if (this.vtGridKeyDown && this.vtGridKeyDown.keyCode === 13) {
                        params.api.setFocusedCell(params?.node.rowIndex, params.column.colId)
                    }
                },
                onCellEditingStarted: params => {
                    params.api.getCellEditorInstances().forEach(cellEditorInstance => {
                        if (this.vtGridKeyDown && this.vtGridKeyDown.keyCode === 13 && cellEditorInstance.component.params.colDef.cellEditor === 'numericCellEditor') {
                            cellEditorInstance.component.focus()
                        }
                    })
                },
                onSelectionChanged: this.onSelectionChanged
            }
        }
    },
    watch: {
        portionLabelToVerify() {
            this.showVerifyClosingTooltip = this.portionLabelToVerify.length > 0
        },
        async sampleId() {
            await this.loadData()
        }
    },
    beforeMount() {
        this.frameworkComponents = {
            vtSelectCellEditor: VtSelectCellEditor,
            vtSelectAsynchronousCellEditor: VtSelectAsynchronousCellEditor,
            datePickerRenderer: DatePickerRenderer,
            numericCellEditor: NumericCellEditor
        }
        this.getRowHeight = 50
        this.rowClassRules = {
            'new-row': params => {
                return !params.data.id
            }
        }
        this.loadTissueProcessorOptions()
    },
    async created() {
        await this.loadData()
    },
    mounted() {
        const portionIdFromRouteParams = this.$route.params.portionId || this.$route.params.porId
        this.selectPortion(portionIdFromRouteParams)
    },
    methods: {
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        async onAssaysViewModalPortionsUpdated() {
            await this.loadPortions()
            this.$emit('portionsUpdated')
        },
        onAssaysViewModalSubportionsUpdated() {
            this.$emit('subportionsUpdated')
        },
        onAssaysViewModalSubportionsAdded() {
            this.$emit('subportionsAdded')
        },
        onSelectedPortionTypeForAddingComboSelect() {
            this.tissueProcessorSelected = null
        },
        visibleRegisterIncidentsContextMenuOption(portion) {
            return this.selectedPortions.length === 1 && this.$_canCreateIncident() && this.$_canBeIncidentCreatedToPortion(portion)
        },
        visibleAddAttachmentContextMenuOption(portion) {
            return this.selectedPortions.length === 1 && this.$_canAttachDocumentToPortion() && this.$_canBeDocumentAttachedToPortion(portion)
        },
        checkToSend(gridElement) {
            this.portionsToSend = gridElement ?? this.selectedPortions
            this.$refs.itemsForShippingModal.show()
        },
        showAssaysModal() {
            const portionsToFocus =
                this.selectedPortions.map((portion) => ({
                    label: portion.label
                }))
            this.$refs.modalAssaysView.show(portionsToFocus)
        },
        async loadData() {
            this.updateLoading(true)
            await this.loadPortionTypes()
            this.loadColumnDefs()
            this.selectedPortionTypeForAdding =
                this.portionTypes.find(portionType => portionType.potOriginalName === 'entity.portiontype.paraffinblock')
            this.giveFocusToVerifyClosingInput()
            this.updateLoading(false)
            await this.loadPortions()
            this.updateGridSubtitle()
        },
        showPortionAttachmentModal() {
            if (this.selectedUniquePortion) {
                this.addAttachments(this.selectedUniquePortion)
            }
        },
        showIncidentFormModal() {
            if (this.selectedUniquePortion) {
                this.$emit('showIncidentFormModal', {
                    casId: this.caseId,
                    samId: this.sampleId,
                    porId: this.selectedUniquePortion.id
                })
            }
        },
        addAttachments(portion) {
            if (this.hasBeenChanged()) {
                this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.unsavedChanges'))
                return
            }

            this.entitiesIdArray = [this.laboratoryId, this.caseId, this.sampleId, portion.id]
            this.$refs.addFileModalWrapper.show()
        },
        async onAttachmentsAdded(attachment) {
            await this.loadData()
            this.$emit('portion-attachment-added', attachment.entityId)
        },
        loadColumnDefs() {
            this.columnDefs = [
                {
                    type: 'customCheckbox',
                    cellStyle: ({ data }) =>
                        data.portionTypeEnum === PORTION_TYPES.VIRTUAL.key
                            ? { 'pointer-events': 'none' }
                            : ''
                },
                {
                    type: 'text',
                    headerName: this.$tc('grossing.portionGrid.portion', 1),
                    field: 'portionNumber',
                    width: 80
                },
                {
                    type: AgGridColumnType.COMMON_PORTION_STATUS,
                    headerName: this.$tc('grossing.portionGrid.status'),
                    field: 'status',
                    width: 120
                },
                {
                    type: 'text',
                    headerName: this.$tc('grossing.portionGrid.numberOfFragments'),
                    field: 'numberOfFragments',
                    width: 120,
                    editable: ({ data }) => !data?.metadata.editionDisabledFields.fragments,
                    cellEditor: 'numericCellEditor',
                    cellEditorParams: () => ({ min: 0, max: 999 }),
                    suppressKeyboardEvent: params => this.supressKeyOnCol(params, 13)
                },
                {
                    type: 'dateTime',
                    headerName: this.$tc('grossing.portionGrid.requestDate'),
                    field: 'requestDate',
                    cellEditor: 'datePickerRenderer'
                },
                {
                    type: AgGridColumnType.TEXT,
                    filterParams: {
                        textFormatter: (params) => TextNormalization.commonSetFilterTextFormatter(params, option => { return option ? `${option.potName}` : '' })
                    },
                    headerName: this.$tc('grossing.portionGrid.portionType') + '*',
                    field: 'portionType',
                    cellEditor: 'vtSelectCellEditor',
                    editable: ({ data }) => !data?.metadata.editionDisabledFields.portionType,
                    suppressKeyboardEvent: params => this.supressKeyOnCol(params, 13),
                    cellRenderer: ({ value }) => value.potName ?? '',
                    cellEditorParams: () => ({
                        options: this.activePortionTypes,
                        trackBy: 'potId',
                        label: 'potName'
                    }),
                    cellClass: params => (!params.data || !params.data.portionType.potId) && this.showErrors ? 'cellUnsaved' : ''
                },
                // TODO: Comment for future development
                /* {
                    type: 'text',
                    headerName: this.$tc('global.laboratory', 1),
                    colId: 'currentLaboratory.labName',
                    field: 'currentLaboratory.labName',
                    exportField: 'currentLaboratory.labName'
                }, */
                {
                    type: AgGridColumnType.TEXT,
                    headerName: this.$tc('grossing.portionGrid.fragmentOrientation'),
                    field: 'fragmentOrientation',
                    cellEditor: 'vtSelectAsynchronousCellEditor',
                    filterParams: {
                        textFormatter: (params) => TextNormalization.commonSetFilterTextFormatter(params, option => { return option ? `${option.forDescription}` : '' })
                    },
                    editable: ({ data }) => !data?.metadata.editionDisabledFields.fragmentOrientation,
                    suppressKeyboardEvent: params => this.supressKeyOnCol(params, 13),
                    cellRenderer: ({ value }) => value ? `<i class="main ${value.forIconName}" aria-hidden="true"></i>${value.forDescription}` : '',
                    onCellValueChanged: () => this.gridApi.refreshCells({ force: true }),
                    cellEditorParams: params => ({
                        optionLoader: () => this.loadFragmentOrientations(params.data.portionType.potId),
                        trackBy: 'forId',
                        label: 'forDescription',
                        icon: 'forIconName'
                    })
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: this.$tc('grossing.portionGrid.tissueProcessor'),
                    field: 'tissueProcessor',
                    cellEditor: 'vtSelectCellEditor',
                    filterParams: {
                        textFormatter: (params) => TextNormalization.commonSetFilterTextFormatter(params, option => { return option ? `${option.device}` : '' })
                    },
                    editable: ({ data }) => !data?.metadata.editionDisabledFields.tissueProcessor,
                    suppressKeyboardEvent: params => this.supressKeyOnCol(params, 13),
                    cellRenderer: ({ value }) => value?.device ?? '',
                    onCellValueChanged: () => this.gridApi.refreshCells({ force: true }),
                    cellEditorParams: () => ({
                        options: this.tissueProcessors,
                        label: 'device'
                    })
                },
                {
                    type: 'text',
                    headerName: this.$tc('grossing.portionGrid.notes'),
                    field: 'notes',
                    editable: ({ data }) => !data?.metadata.editionDisabledFields.notes,
                    suppressKeyboardEvent: params => this.supressKeyOnCol(params, 13)
                },
                {
                    type: 'incident',
                    headerName: this.$tc('grossing.portionGrid.incidents'),
                    field: 'incidentsCount',
                    filter: false,
                    width: 55,
                    headerTooltip: this.$tc('grossing.portionGrid.incidents'),
                    headerComponentParams: {
                        template: buildIconHeaderTemplate('far fa-exclamation-triangle')
                    }
                },
                {
                    type: 'attachmentCount',
                    headerName: this.$tc('grossing.portionGrid.attachments'),
                    field: 'attachmentsCount',
                    width: 55,
                    filter: false,
                    headerTooltip: this.$tc('grossing.portionGrid.attachments'),
                    headerComponentParams: {
                        template: buildIconHeaderTemplate('far fa-paperclip')
                    },
                    onCellClicked: cell => this.$refs.attachmentTreeModal.showPortion(cell.data.id)
                },
                {
                    type: 'date',
                    headerName: this.$tc('grossing.portionGrid.firstPrinting'),
                    field: 'printLabelDate',
                    width: 150
                }
            ]

            const hasUnauthorizedBillableConceptsColumnField = 'hasUnauthorizedBillableConcepts'
            if (
                ModuleOperationGuard.canAccessBilling &&
                !this.columnDefs.find(column => column.field === hasUnauthorizedBillableConceptsColumnField)
            ) {
                this.columnDefs.push({
                    type: AgGridColumnType.HAS_UNAUTHORIZED_BILLABLE_CONCEPTS,
                    field: hasUnauthorizedBillableConceptsColumnField,
                    flex: 1,
                    minWidth: 55
                })
            }
        },
        selectPortion(porId) {
            if (this.gridApi) {
                this.gridApi.forEachNode(node => {
                    if (node.data.id === porId) {
                        if (node.selected) {
                            this.$_showWarningModal(this.$tc('grossing.portionGrid.blockAlreadySelected'))
                        } else {
                            node.setSelected(true)
                        }
                    }
                })
            } else {
                this.initPorIdSelect = porId
            }
        },
        updateGridSubtitle() {
            this.gridSubtitle = `${this.selectedPortions.length} ${this.$tc('grossing.content.of')} ${this.portions.length} ${this.$tc('grossing.content.selected')}`
        },
        async loadFragmentOrientations(portionTypeId) {
            if (!portionTypeId) {
                return []
            }
            return fragmentOrientationService.findAll(this.laboratoryId, portionTypeId)
        },
        async loadTissueProcessorOptions() {
            const params = {
                operations: [
                    PortionOperation.TO_TISSUE_PROCESSOR,
                    PortionOperation.PROCESS
                ]
            }
            const [error, response] = await this.toCall(commonDeviceService.search(this.laboratoryIdSample, params))
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            }
            this.tissueProcessors = response
        },
        async loadPortionTypes() {
            {
                const [error, response] = await this.toCall(portionTypeService.findAllPhysicalPortionTypes())
                if (error) {
                    this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.loadingPortionTypes'))
                    return
                }
                this.activePortionTypes = response
            }
            {
                const [error, response] = await this.toCall(portionTypeService.findAll())
                if (error) {
                    this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.loadingPortionTypes'))
                    return
                }
                this.portionTypes = response
                this.virtualPortionType = this.portionTypes.find(portionType => portionType.portionTypeEnum === PORTION_TYPES.VIRTUAL.key)
            }
        },
        async loadPortions() {
            const [error, response] = await this.toCall(sampleProcessingPortionService.findAllPortionEditableGrid(
                this.laboratoryId,
                this.caseId,
                this.sampleId))
            if (error) {
                this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.loadingPortions'))
                return
            }
            this.portions = response.filter((portion) => portion.portionTypeEnum !== PORTION_TYPES.VIRTUAL.key)
            if (this.initPorIdSelect) {
                this.$nextTick(() => this.selectPortion(this.initPorIdSelect))
            }
            this.originalPortions = cloneDeep(this.portions)
            this.selectedPortions = []
        },
        backspaceFunction(params) {
            let keyCode = params.event.keyCode
            if (params && keyCode === 8 && !params.editing) {
                let field = params.colDef.field
                params.node.data[field] = null
                this.gridApi.refreshCells({ force: true })
                return true
            }
        },
        getPortionsContextMenu(params) {
            const portion = params?.node.data
            return [
                {
                    name: this.$tc('grossing.portionGrid.deletePortion'),
                    cssClasses:
                        this.$_canDeletePortion() &&
                        !this.disabledPortionDeletion
                            ? '' : ['not-show'],
                    action: () =>
                        this.selectedPortions.length
                            ? this.deleteSelectedPortions()
                            : this.askDeletePortions([portion])
                },
                {
                    name: this.$tc('grossing.portionGrid.printPortion'),
                    cssClasses:
                        this.$_canPrintPortionCassette() &&
                        !this.disabledPrintPortionsCassette
                            ? '' : ['not-show'],
                    action: () => this.showPrintModal(this.selectedPortions)
                },
                // TODO: Comment for future development
                /* {
                    name: this.$tc('shipments.actions.checkToSend'),
                    cssClasses: !this.selectedPortions.length && this.canCreateShipmentRequests() ? '' : ['not-show'],
                    action: () => this.checkToSend([portion])
                }, */
                {
                    name: this.$tc('grossing.portionGrid.registerIncidents'),
                    cssClasses: this.visibleRegisterIncidentsContextMenuOption(portion) ? '' : ['not-show'],
                    action: () => this.$emit('showIncidentFormModal', {
                        casId: this.caseId,
                        samId: this.sampleId,
                        porId: portion.id
                    })
                },
                {
                    name: this.$tc('hoverBtn.seeIncidence'),
                    cssClasses: this.selectedPortions.length > 1 ? ['not-show'] : '',
                    action: () => this.$emit('showIncidentsModal', {
                        coreEntity: 'PORTION',
                        casId: this.caseId,
                        samId: this.sampleId,
                        porId: portion.id,
                        portionTypeEnum: portion.portionType.portionTypeEnum
                    })
                },
                {
                    name: this.$tc('grossing.portionGrid.addAttachment'),
                    cssClasses: this.visibleAddAttachmentContextMenuOption(portion) ? '' : ['not-show'],
                    action: () => this.addAttachments(portion)
                },
                {
                    name: this.$tc('grossing.portionGrid.seeAttachments'),
                    cssClasses: this.selectedPortions.length > 1 ? ['not-show'] : '',
                    action: () => this.$refs.attachmentTreeModal.showPortion(portion.id)
                },
                {
                    name: this.$t('global.showInGlobalLocator'),
                    cssClasses: this.selectedPortions.length > 1 ? ['not-show'] : '',
                    action: () => this.showInGlobalLocator(this.selectedPortions[0].label)
                }
            ]
        },
        supressKeyOnCol(params, keyToSupress) {
            return params.editing &&
                params.event.keyCode === keyToSupress &&
                params.colDef.cellEditor === 'vtSelectCellEditor'
        },
        onAfterMasterRowExpanded(api, detailNode, parentDetail) {
            detailNode.data.expandedHeight = this.calculateDetailHeight(detailNode)
            detailNode.setRowHeight(detailNode.data.expandedHeight)
            api.onRowHeightChanged()
            if (parentDetail) {
                this.onAfterMasterRowExpanded(
                    parentDetail.gridApi,
                    parentDetail,
                    parentDetail.data.parentDetail
                )
            }
        },
        calculateDetailHeight(detailNode) {
            let totalHeight = 0
            detailNode.data.children.forEach(child => {
                totalHeight += 28
                if (child.expandedHeight) {
                    totalHeight += child.expandedHeight
                }
            })
            return totalHeight + 80
        },
        validateRows() {
            this.showErrors = false
            return !this.portions.find(portion => !portion.portionType)
        },
        hasBeenChanged() {
            if (this.portions.length !== this.originalPortions.length) {
                return true
            }

            const mappedOriginalPortions =
                this.originalPortions.map(
                    originalPortion => this.removeExternalOptionFromEntityObjects(originalPortion))
            const firstPortionWithChanges =
                this.portions.find(
                    portion => !some(mappedOriginalPortions, this.removeExternalOptionFromEntityObjects(portion)))
            return !!firstPortionWithChanges
        },
        async save() {
            if (!this.hasBeenChanged()) {
                return true
            }
            if (!this.validateRows()) {
                this.showErrors = true
                this.gridApi.refreshCells({ force: true })
                this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.checkGridForErrors'))
                return false
            }
            const portionsToUpdate = []
            this.portions.forEach(portion => {
                if (portion.id && !this.originalPortions.some(originalPortion => originalPortion === portion)) {
                    portionsToUpdate.push(portion)
                }
            })

            for (let portion of portionsToUpdate) {
                const sampleProcessingPortionRequest = {
                    id: portion.id,
                    numberOfFragments: portion.numberOfFragments,
                    fragmentOrientationId: portion.fragmentOrientation?.forId,
                    portionTypeId: portion.portionType?.potId,
                    tissueProcessorId: portion.tissueProcessor && !(portion.portionType.portionTypeEnum === PortionTypeEnum.FROZEN_BLOCK) ? portion.tissueProcessor.id : null,
                    notes: portion.notes,
                    originalConcurrencyUpdatedAt: portion.originalConcurrencyUpdatedAt
                }
                const successfulUpdate = await this.updateOnePortion(sampleProcessingPortionRequest)
                if (!successfulUpdate) {
                    return false
                }
            }

            this.showToastSuccessGlobal()
            await this.loadData()
            return true
        },
        async updateOnePortion(portion) {
            const [err] = await this.toCall(sampleProcessingPortionService.updateBatch(this.laboratoryId, this.caseId, this.sampleId, [portion]))
            if (err) {
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$tc('global.block', 1) + ` - ${portion.label}`, err, () => {
                        portion.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.updateOnePortion(portion)
                    })
                } else {
                    this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.updatingPortions'))
                    return false
                }
            }
            return true
        },
        askDeletePortions(portions) {
            const message = getPortionDeletionConfirmationMessage(portions)
            this.portionDeletionConfirmationMessage = message !== '' ? this.$tc(message) : ''

            this.$nextTick(() => {
                this.$refs.onDeletingPortion.ask(() => this.deletePortions(portions))
            })
        },
        async deletePortions(portions) {
            if (!portions || !portions.length) {
                return
            }
            if (this.hasBeenChanged()) {
                this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.unsavedChanges'))
                return
            }
            try {
                this.updateLoading(true)

                const errors = await sampleProcessingPortionService.deleteBatch(this.laboratoryId, this.caseId, this.sampleId, portions)
                if (errors && errors.length > 0) {
                    // Error deleting, check details
                    const distinctErrors = Array.from(new Set(errors.map(element => element.errorMessage)))
                    let errorMessage = this.$tc('grossing.portionGrid.error.expectedDeleteError')
                    distinctErrors.forEach(elementWithError => {
                        errorMessage = errorMessage + `<p>${elementWithError}</p>`
                    })
                    this.showErrorModalGlobal(errorMessage)
                }
            } catch (e) {
                // Unexpected error
                this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.unexpectedDeleteError'))
            } finally {
                this.updateLoading(false)
                await this.loadData()
            }
        },
        deleteSelectedPortions() {
            this.askDeletePortions(this.selectedPortions)
        },
        async showPrintModal(selectedPortions, printWithModal) {
            let portionsToPrint = selectedPortions
            if (this.hasBeenChanged()) {
                portionsToPrint = []
                // eslint-disable-next-line no-new
                await new Promise((resolve) => {
                    this.$refs.saveAndPrintConfirmation.ask(async () => {
                        await this.save()

                        selectedPortions.forEach(portion => {
                            const originalPortionSelectionIdsList = selectedPortions.map(eachSelection => {
                                return eachSelection.porId
                            })
                            if (originalPortionSelectionIdsList.includes(portion.porId)) {
                                portionsToPrint.push(portion)
                            }
                        })
                        resolve()
                    }, () => {
                        resolve()
                    })
                })
            }
            if (portionsToPrint.length !== 0) {
                await this.printCassetteLabels(portionsToPrint, printWithModal)
            }
        },
        async showPrintModalFromSelected(printWithModal) {
            if (this.selectedPortions.length) {
                await this.showPrintModal(this.selectedPortions, printWithModal)
            } else {
                this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.printOnlyOne'))
            }
        },
        async printCassetteLabels(portions, printWithModal) {
            await this.$refs.printComponent.printCassetteLabels(
                this.laboratoryId,
                this.caseId,
                this.sampleId,
                portions,
                this.priority,
                this.sampleTypeId,
                this.topographicCodeId,
                printWithModal
            )
            await this.loadPortions()
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.gridApi.showLoadingOverlay()
        },
        onSelectionChanged({ api }) {
            this.selectedPortions = api.getSelectedRows()
        },
        async addNewRows() {
            if (this.hasBeenChanged()) {
                this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.unsavedChanges'))
                return
            }

            this.creatingPortions = true
            let portions = []
            if (this.selectedPortions.length) {
                let selectedRows = cloneDeep(this.selectedPortions)
                selectedRows.forEach(row => {
                    row.id = null
                    row.portionNumber = null
                    row.requestDate = null
                    row.incidentsCount = null
                    row.attachmentsCount = null
                })
                if (this.cloneNumber) {
                    selectedRows.forEach(row => {
                        portions.push(
                            ...Array.from({ length: this.cloneNumber }, () => cloneDeep(row)))
                    })
                }
            } else {
                if (this.cloneNumber) {
                    portions.push(
                        ...Array.from({ length: this.cloneNumber }, () => ({
                            portionTypeId: this.selectedPortionTypeForAdding.potId,
                            tissueProcessor: this.tissueProcessorSelected
                        }))
                    )
                } else {
                    portions.push({})
                }
            }
            await this.addNewPortions(portions)
            this.creatingPortions = false
        },
        giveFocusToVerifyClosingInput() {
            if (!this.$_disabledVerifyPortions(this.portions)) {
                this.$nextTick(() => this.$refs.verifyClosingInput.focus())
            }
        },
        async verifyClosedCassetteByLabel() {
            if (!this.portionLabelToVerify) {
                return
            }
            try {
                const portionLabel = this.portionLabelToVerify.trim()
                const foundPortion = this.portions.find(portion => portion.label === portionLabel)
                if (!foundPortion) {
                    this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.verifyingCassetteClosedNotFound'))
                } else if (!this.$_canBePortionVerified(foundPortion)) {
                    this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.noValidationRequired'))
                } else {
                    await sampleProcessingPortionService.verifyCassetteClosedByLabel(
                        this.laboratoryId,
                        this.caseId,
                        this.sampleId,
                        portionLabel
                    )
                    this.portionLabelToVerify = ''
                    await this.loadData()
                }
            } catch (e) {
                this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.verifyingCassetteClosed'))
            }
        },
        mapPortionsToCreateBatchRequest(portions) {
            let portionsToRequest = []
            portions.forEach((portion) => {
                portionsToRequest.push({
                    portionTypeId: (portion.portionType ? portion.portionType.potId : portion.portionTypeId),
                    tissueProcessorId: portion.tissueProcessor && portion.tissueProcessor.id
                })
            })
            return portionsToRequest
        },
        async addNewPortions(portions) {
            if (portions.length > 0) {
                const request = this.mapPortionsToCreateBatchRequest(portions)
                this.updateLoading(true)
                const [error] = await this.toCall(
                    sampleProcessingPortionService.createBatch(
                        this.laboratoryId,
                        this.caseId,
                        this.sampleId,
                        request
                    )
                )
                this.updateLoading(false)
                if (error) {
                    this.showErrorModalGlobal(this.$tc('grossing.portionGrid.error.creatingPortions'))
                    return
                }
                this.showToastSuccessGlobal()
                this.$emit('portionsUpdated')
                await this.loadData()
            }
        },
        openCancellationModalForm() {
            this.$refs.laboratoryCancellationModalForm.show()
        }
    }
}
</script>

<style lang="scss">
.grossing-portion-editable-grid {
    display: flex;
    flex-direction: column;
    height: 100%;

    .grossing-portion-grid {
        flex-grow: 1;
        min-height: 200px;
    }
}
.grossing-portion-editable-grid .ag-root-wrapper-body.ag-layout-normal {
  padding-left: 0;
}

.grossing-portion-editable-grid .sr-panel-filters {
  padding-right: 1%;
}

.grossing-portion-verify-closing-input{
    position: relative;
}

.grossing-portion-editable-grid .verify::before{
    content: '\f463';
    font-weight: 300;
    font-family: 'Font Awesome 5 Pro', sans-serif;
    position: absolute;
    z-index: 4;
    left: 8px;
    font-size: 0.9rem;
}

.grossing-portion-editable-grid .verify input {
  width: 150px;
  padding: 0 30px;
  border-radius: 0.2rem !important;
}

.grossing-portion-editable-grid .verify .btn {
  position: absolute;
  right: 5px;
  z-index: 1;
}

.grossing-portion-editable-grid .header-subtitle {
  text-transform: none !important;
}
</style>

<style lang="scss" scoped>
.subtitle {
  font-size: font-size(very-tiny);
}

.portion-type-selector {
  width: 150px;
}

.clone-number {
  width: 50px;
}
</style>
